import { getSafeEnv } from "@/utils/env";
import { BaseRepositoryV2 } from "./base-repository-v2";

/**
 * Repository factory with singleton pattern for performance optimization
 * Ensures only one instance of each repository type per base URL
 */
// biome-ignore lint/complexity/noStaticOnlyClass: Factory pattern requires static methods with shared state
class RepositoryFactory {
  private static instances: Map<string, BaseRepositoryV2> = new Map();

  /**
   * Gets or creates a singleton repository instance for the given base URL
   * @param baseUrl - The base URL for the API endpoint
   * @returns BaseRepositoryV2 instance
   */
  static getInstance(baseUrl?: string): BaseRepositoryV2 {
    const env = getSafeEnv();
    const url = baseUrl || env.NEXT_PUBLIC_API_URL || "";
    const key = url;

    if (!RepositoryFactory.instances.has(key)) {
      RepositoryFactory.instances.set(key, new BaseRepositoryV2(url));
    }

    const instance = RepositoryFactory.instances.get(key);
    if (!instance) {
      throw new Error(`Failed to create repository instance for URL: ${url}`);
    }
    return instance;
  }

  /**
   * Gets or creates a core API repository instance
   * @returns BaseRepositoryV2 instance for core API
   */
  static getCoreInstance(): BaseRepositoryV2 {
    const env = getSafeEnv();
    return RepositoryFactory.getInstance(env.NEXT_PUBLIC_API_CORE_URL);
  }

  /**
   * Gets or creates a catalog API repository instance
   * @returns BaseRepositoryV2 instance for catalog API
   */
  static getCatalogInstance(): BaseRepositoryV2 {
    const env = getSafeEnv();
    return RepositoryFactory.getInstance(`${env.NEXT_PUBLIC_API_CORE_URL}/catalog-mongo-web`);
  }

  /**
   * Gets or creates a catalog graph API repository instance
   * @returns BaseRepositoryV2 instance for catalog graph API
   */
  static getCatalogGraphInstance(): BaseRepositoryV2 {
    const env = getSafeEnv();
    return RepositoryFactory.getInstance(`${env.NEXT_PUBLIC_API_CORE_URL}/catalog-graph-web`);
  }

  /**
   * Clears all cached instances (useful for testing or memory cleanup)
   */
  static clearInstances(): void {
    RepositoryFactory.instances.clear();
  }
}

export default RepositoryFactory;
